import React from 'react';
import { CreditCard, Calendar, Building2, Hash, User, CheckCircle, XCircle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

import { Merchant } from '../lib/api/types';

interface MerchantDetailModalProps {
  merchant: Merchant | null;
  isOpen: boolean;
  onClose: () => void;
}

export const MerchantDetailModal: React.FC<MerchantDetailModalProps> = ({
  merchant,
  isOpen,
  onClose,
}) => {
  if (!merchant) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Merchant Details
          </DialogTitle>
          <DialogDescription>
            Detailed information for merchant "{merchant.merchantName}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Merchant Name</label>
                  <p className="text-sm font-medium">{merchant.merchantName}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Merchant ID</label>
                  <p className="text-sm font-mono">{merchant.id}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Account Number</label>
                  <p className="text-sm font-mono">{merchant.accountNumber}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Biller ID</label>
                  <p className="text-sm font-mono">{merchant.billerId}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Partner Information */}
          {(merchant.partnerId || merchant.partnerName) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="h-4 w-4" />
                  Partner Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {merchant.partnerId && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Partner ID</label>
                      <p className="text-sm font-mono">{merchant.partnerId}</p>
                    </div>
                  )}

                  {merchant.partnerName && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Partner Name</label>
                      <p className="text-sm">{merchant.partnerName}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security & Authentication */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Hash className="h-4 w-4" />
                Security & Authentication
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Auth Token</label>
                <p className="text-sm font-mono break-all bg-muted p-2 rounded mt-1">
                  {merchant.authToken}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar className="h-4 w-4" />
                Timestamps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created At</label>
                  <p className="text-sm">{formatDate(merchant.createdAt)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                  <p className="text-sm">{formatDate(merchant.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
